export interface WhatsNewAnnouncementAction {
  label: string;
  to: string;
}

export interface WhatsNewAnnouncementTarget {
  exact?: string[];
  prefixes?: string[];
}

export interface WhatsNewAnnouncement {
  key: string;
  version: string;
  title: string;
  body: string;
  primaryAction?: WhatsNewAnnouncementAction;
  target: WhatsNewAnnouncementTarget;
}

const DISMISSED_ANNOUNCEMENTS_STORAGE_KEY = "veronics.whatsNew.dismissed";

export const whatsNewAnnouncements: WhatsNewAnnouncement[] = [];

export const getWhatsNewAnnouncementDismissalId = (announcement: Pick<WhatsNewAnnouncement, "key" | "version">) =>
  `${announcement.key}:${announcement.version}`;

const readDismissedAnnouncementIds = () => {
  if (typeof window === "undefined") {
    return new Set<string>();
  }

  try {
    const storedValue = window.localStorage.getItem(DISMISSED_ANNOUNCEMENTS_STORAGE_KEY);

    if (storedValue === null) {
      return new Set<string>();
    }

    const parsedValue = JSON.parse(storedValue);

    if (!Array.isArray(parsedValue)) {
      return new Set<string>();
    }

    return new Set(parsedValue.filter((value): value is string => typeof value === "string"));
  } catch {
    return new Set<string>();
  }
};

const writeDismissedAnnouncementIds = (dismissedIds: Set<string>) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(DISMISSED_ANNOUNCEMENTS_STORAGE_KEY, JSON.stringify([...dismissedIds]));
  } catch {
    // localStorage can be disabled or full; announcements should not block the app.
  }
};

const trimTrailingSlash = (path: string) => (path.length > 1 ? path.replace(/\/+$/, "") : path);

export const isWhatsNewAnnouncementTargetedToPath = (announcement: WhatsNewAnnouncement, pathname: string) => {
  const normalizedPathname = trimTrailingSlash(pathname);
  const exactTargets = announcement.target.exact?.map(trimTrailingSlash) ?? [];
  const prefixTargets = announcement.target.prefixes?.map(trimTrailingSlash) ?? [];

  return (
    exactTargets.includes(normalizedPathname) ||
    prefixTargets.some((prefix) => normalizedPathname === prefix || normalizedPathname.startsWith(`${prefix}/`))
  );
};

export const isWhatsNewAnnouncementDismissed = (announcement: WhatsNewAnnouncement) => {
  const dismissedIds = readDismissedAnnouncementIds();

  return dismissedIds.has(getWhatsNewAnnouncementDismissalId(announcement));
};

export const dismissWhatsNewAnnouncement = (announcement: WhatsNewAnnouncement) => {
  const dismissedIds = readDismissedAnnouncementIds();
  dismissedIds.add(getWhatsNewAnnouncementDismissalId(announcement));
  writeDismissedAnnouncementIds(dismissedIds);
};

export const getActiveWhatsNewAnnouncement = (announcements: WhatsNewAnnouncement[], pathname: string) =>
  announcements.find(
    (announcement) =>
      isWhatsNewAnnouncementTargetedToPath(announcement, pathname) && !isWhatsNewAnnouncementDismissed(announcement),
  ) ?? null;
